'use client';

import React from 'react';
import { Box, Grid, type BoxProps, type GridProps } from '@mui/material';
import SkeletonCard from 'sharedComponents/Skeleton/SkeletonCard';

type ChatInfiniteScrollPropTypes = {
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  children: React.ReactNode | React.ReactNode[];
  childContainerStyles?: BoxProps['sx'];
  parentContainerStyles?: GridProps['sx'];
  onLoadOlderMessages: () => void;
  skeletonCount?: number;
  childrenGridProps?: GridProps;
  skeletonComponent?: React.ElementType;
};

const getSkeletons = (
  skeletonCount: number,
  SkeletonComponent?: React.ElementType
) => {
  return Array.from({ length: skeletonCount }).map((_, index) => (
    <Grid item xs={12} key={index}>
      {SkeletonComponent ? <SkeletonComponent /> : <SkeletonCard />}
    </Grid>
  ));
};

function ChatInfiniteScroll({
  isLoading = false,
  isFetchingNextPage = false,
  children,
  childContainerStyles = {},
  parentContainerStyles = {},
  onLoadOlderMessages,
  skeletonCount = 1,
  childrenGridProps,
  skeletonComponent: SkeletonComponent,
}: ChatInfiniteScrollPropTypes) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const firstElementRef = React.useRef<HTMLDivElement>(null);
  const lastElementRef = React.useRef<HTMLDivElement>(null);

  // // Set up intersection observer for top element (to load older messages)
  React.useEffect(() => {
    console.log('element refs', {
      first: firstElementRef.current,
      last: lastElementRef.current,
    });
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        console.log('last element visible', {
          first: entry.target === firstElementRef.current,
          last: entry.target === lastElementRef.current,
        });
        if (entry.target === firstElementRef.current) {
        }
        if (entry.target === lastElementRef.current) {
          if (entry.isIntersecting) {
            onLoadOlderMessages();
          }
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, {
      root: containerRef.current,
      threshold: 0.1,
    });

    if (firstElementRef.current) {
      observer.observe(firstElementRef.current);
    }
    if (lastElementRef.current) {
      observer.observe(lastElementRef.current);
    }

    return () => {
      if (firstElementRef.current) {
        observer.unobserve(firstElementRef.current);
      }
      if (lastElementRef.current) {
        observer.unobserve(lastElementRef.current);
      }
    };
  }, [onLoadOlderMessages]);

  React.useEffect(() => {
    if (isLoading) {
      //initial loading
      return;
    }

    if (!firstElementRef) {
      return;
    }

    firstElementRef.current?.scrollIntoView({
      block: 'end',
    });
  }, [isLoading]);

  const childrenContent = React.useMemo(() => {
    if (isLoading) {
      return <>{getSkeletons(skeletonCount, SkeletonComponent)}</>;
    }

    if (Array.isArray(children)) {
      let childrenToRender: React.ReactNode[] = children;
      if (isFetchingNextPage) {
        childrenToRender = [
          ...children,
          ...getSkeletons(skeletonCount, SkeletonComponent),
        ];
      }

      return (
        <>
          {childrenToRender?.map((child, index) => (
            <Box
              id={'messagecontainer-id ' + index}
              maxWidth='800px'
              key={'messagecontainer-id ' + index}
              ref={
                index === 0
                  ? firstElementRef
                  : index === children.length - 1
                    ? lastElementRef
                    : null
              }
              sx={childContainerStyles}
            >
              {child}
            </Box>
          ))}
        </>
      );
    }
    return children;
  }, [
    SkeletonComponent,
    childContainerStyles,
    children,
    isFetchingNextPage,
    isLoading,
    skeletonCount,
  ]);

  return (
    <Box
      sx={{
        maxWidth: '800px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column-reverse',
        overflow: 'auto',
        scrollbarWidth: 'none',
        height: '100%',
        ...parentContainerStyles,
      }}
      ref={containerRef}
    >
      {childrenContent}
    </Box>
  );
}

export default ChatInfiniteScroll;
